import { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download, File, X } from 'lucide-react';
import { PageTitle} from '../../../../_metronic/layout/core'

interface ImportedData {
  fileName: string;
  rowCount: number;
  timestamp: string;
  preview: any[];
}

export function ImportPage() {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [importedData, setImportedData] = useState<ImportedData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      setUploadedFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleProcessFile = () => {
    if (!uploadedFile) return;

    setIsProcessing(true);

    setTimeout(() => {
      const mockPreviewData = [
        { ean: '7506306241183', nome: 'Dove Aero 150ml', estoqueAtual: 34, vendasPeriodo: 84, diasPeriodo: 30, precoUnitario: 12.90, fornecedor: 'Unilever Brasil' },
        { ean: '7791293032436', nome: 'Rexona Aero 150ml', estoqueAtual: 0, vendasPeriodo: 75, diasPeriodo: 30, precoUnitario: 11.50, fornecedor: 'Unilever Brasil' },
        { ean: '7894000030470', nome: 'Maionese 200g Sachet', estoqueAtual: 12, vendasPeriodo: 90, diasPeriodo: 30, precoUnitario: 3.20, fornecedor: 'Hellmann\'s Distribuidor' },
        { ean: '7894000050034', nome: 'Maionese 500g Trad', estoqueAtual: 752, vendasPeriodo: 1950, diasPeriodo: 30, precoUnitario: 7.80, fornecedor: 'Hellmann\'s Distribuidor' },
        { ean: '7891150064331', nome: 'OMO 1,6kg', estoqueAtual: 256, vendasPeriodo: 195, diasPeriodo: 30, precoUnitario: 18.90, fornecedor: 'Unilever Brasil' },
      ];

      setImportedData({
        fileName: uploadedFile.name,
        rowCount: mockPreviewData.length,
        timestamp: new Date().toLocaleString('pt-BR'),
        preview: mockPreviewData
      });

      setIsProcessing(false);
    }, 2000);
  };

  const handleReset = () => {
    setUploadedFile(null);
    setImportedData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'EAN,Nome do Produto,Estoque Atual,Vendas no Período,Dias do Período,Preço Unitário,Fornecedor\n7506306241183,Dove Aero 150ml,34,84,30,12.90,Unilever Brasil\n7791293032436,Rexona Aero 150ml,0,75,30,11.50,Unilever Brasil';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_importacao_produtos.csv';
    link.click();
  };

  return (
    <div>
        <PageTitle>Importar Dados</PageTitle>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Importar Dados</h1>
        <p className="text-gray-600 mt-2">Faça upload de planilhas Excel ou CSV com os dados dos produtos</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="font-semibold text-gray-900">Formatos Aceitos</h3>
          </div>
          <p className="text-sm text-gray-600">Excel (.xlsx, .xls) e CSV (.csv)</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-700" />
            </div>
            <h3 className="font-semibold text-gray-900">Campos Obrigatórios</h3>
          </div>
          <p className="text-sm text-gray-600">EAN, Nome, Estoque, Vendas, Dias</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Download className="w-5 h-5 text-purple-700" />
            </div>
            <h3 className="font-semibold text-gray-900">Template Disponível</h3>
          </div>
          <button
            onClick={downloadTemplate}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Baixar modelo
          </button>
        </div>
      </div>

      {!importedData ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="flex flex-col items-center">
              <div className="p-4 bg-gray-100 rounded-full mb-4">
                <Upload className="w-12 h-12 text-gray-600" />
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {uploadedFile ? 'Arquivo Selecionado' : 'Arraste e solte seu arquivo aqui'}
              </h3>

              {uploadedFile ? (
                <div className="mb-4">
                  <div className="flex items-center space-x-2 bg-gray-50 px-4 py-2 rounded-lg">
                    <File className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">{uploadedFile.name}</span>
                    <button
                      onClick={handleReset}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Tamanho: {(uploadedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              ) : (
                <p className="text-gray-600 mb-6">
                  ou clique para selecionar
                </p>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />

              {!uploadedFile ? (
                <label
                  htmlFor="file-upload"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer font-medium"
                >
                  Selecionar Arquivo
                </label>
              ) : (
                <button
                  onClick={handleProcessFile}
                  disabled={isProcessing}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <span className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Processando...</span>
                    </span>
                  ) : (
                    'Processar Arquivo'
                  )}
                </button>
              )}

              <p className="text-xs text-gray-500 mt-4">
                Formatos suportados: Excel (.xlsx, .xls) e CSV (.csv) - Máximo 10MB
              </p>
            </div>
          </div>

          <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-700 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Estrutura do Arquivo</h4>
                <p className="text-sm text-blue-800 mb-3">
                  Sua planilha deve conter as seguintes colunas:
                </p>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li><strong>EAN</strong>: Código de barras do produto</li>
                  <li><strong>Nome do Produto</strong>: Descrição completa</li>
                  <li><strong>Estoque Atual</strong>: Quantidade disponível</li>
                  <li><strong>Vendas no Período</strong>: Total de vendas</li>
                  <li><strong>Dias do Período</strong>: Período analisado em dias</li>
                  <li><strong>Preço Unitário</strong>: Valor de compra (opcional)</li>
                  <li><strong>Fornecedor</strong>: Nome do fornecedor (opcional)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Importação Concluída</h3>
                  <p className="text-sm text-gray-600">
                    {importedData.rowCount} produtos importados de {importedData.fileName}
                  </p>
                </div>
              </div>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Nova Importação
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Arquivo</p>
                <p className="font-semibold text-gray-900">{importedData.fileName}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Registros</p>
                <p className="font-semibold text-gray-900">{importedData.rowCount} produtos</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Data/Hora</p>
                <p className="font-semibold text-gray-900">{importedData.timestamp}</p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-700" />
                <p className="text-sm text-green-800">
                  Os dados foram processados com sucesso e estão prontos para uso no sistema.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Preview dos Dados Importados</h3>
              <p className="text-sm text-gray-600 mt-1">Mostrando os primeiros {importedData.preview.length} registros</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">EAN</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nome</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Estoque</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Vendas</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Período</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Preço</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Fornecedor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {importedData.preview.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-mono">{row.ean}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{row.nome}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.estoqueAtual}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.vendasPeriodo}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.diasPeriodo} dias</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">R$ {row.precoUnitario.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{row.fornecedor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
